import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogSections } from 'components/create-dialog/CreateDialogSection';
import ContentMenu, { ContentMenuRow } from 'components/forms/ContentMenu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useIsMobile } from 'hooks/useIsMobile';
import React, { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { useSelector } from 'store';
import { cn } from 'utils/cn';
import { getIdLink } from 'utils/linkUtil';
import { SectionProps } from './Section';
import { useHighlightedFieldsCounts } from './SectionActionsContext';
import { SubSectionProps } from './SubSection';

type Props = {
    children:
        | (React.ReactElement<SectionProps> | React.ReactElement<SectionProps>[] | null)[]
        | React.ReactElement<SectionProps>
        | null;
    showMenu?: boolean;
    readonly?: boolean;
    sectionErrors?: Record<string, boolean>;
    rightSidebar?: React.ReactNode;
};

/**
 * The menu column beside the sections, and the sidebar after them when there is one. In focused edit
 * mode a viewport wide enough for slack centres the 960px form column between two flexible tracks.
 */
function menuGridClass(isFocused: boolean, hasSidebar: boolean) {
    return cn(
        'grid gap-[6%]',
        hasSidebar ? 'grid-cols-[192px_1fr_auto]' : 'grid-cols-[192px_1fr]',
        isFocused &&
            (hasSidebar
                ? 'figma:grid-cols-[1fr_minmax(0,960px)_auto_1fr] figma:gap-0'
                : 'figma:grid-cols-[1fr_minmax(0,960px)_1fr] figma:gap-0')
    );
}

/**
 * Parses children and returns rows for ContentMenu
 *
 * @returns ContentMenuRow[]
 */
const getRowsFromChildren = (
    children: React.ReactElement<SectionProps>[] | React.ReactElement<SectionProps>
): ContentMenuRow[] => {
    const rows: ContentMenuRow[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.JSXElementConstructor's type param is the component's props; we mean "any component"
    let sectionComponents: React.ReactElement<SectionProps, string | React.JSXElementConstructor<any>>[] = [];
    if (Array.isArray(children)) {
        sectionComponents = children;
    } else {
        sectionComponents = [children];
    }
    sectionComponents.forEach((section) => {
        const row: ContentMenuRow = {
            title: section.props.title,
            subtitles: [],
            noTranslate: section.props.noTranslation,
        };
        if (Array.isArray(section.props.children)) {
            section.props.children.forEach((child) => {
                if (isValidElement(child) && getDisplayName(child.type) === 'SubSection') {
                    row.subtitles.push((child.props as SubSectionProps).title);
                }
            });
        } else {
            if (
                isValidElement(section.props.children) &&
                getDisplayName(section.props.children.type) === 'SubSection'
            ) {
                row.subtitles.push((section.props.children.props as SubSectionProps).title);
            }
        }
        rows.push(row);
    });
    return rows;
};

const getDisplayName = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.JSXElementConstructor's type param is the component's props; any matches React's own union shape
    type: string | React.JSXElementConstructor<any>
): string | undefined => (typeof type === 'string' ? type : (type as { displayName?: string }).displayName);

const flattenChildren = (nodes: React.ReactNode): React.ReactElement<SectionProps>[] => {
    const result: React.ReactElement<SectionProps>[] = [];
    React.Children.forEach(nodes, (child) => {
        if (!isValidElement(child)) return;
        const childProps = child.props as { isHidden?: boolean; children?: React.ReactNode };
        if (getDisplayName(child.type) === 'Section' && !childProps.isHidden) {
            result.push(child as React.ReactElement<SectionProps>);
        } else if (childProps.children) {
            result.push(...flattenChildren(childProps.children));
        }
    });
    return result;
};

/**
 * A form's sections beside their menu. The create dialog lists them as accordions down its body instead, under
 * a Required section that holds every required field: no menu, no sidebar, and no `data-form-body`, which the
 * dialog sets on a box that does not clip.
 */
const SectionLayout = (props: Props) => {
    const inCreateDialog = useInCreateDialog();

    return inCreateDialog ? (
        <CreateDialogSections>{props.children}</CreateDialogSections>
    ) : (
        <PageSectionLayout {...props} />
    );
};

function PageSectionLayout({ children, showMenu = true, readonly, sectionErrors, rightSidebar }: Props) {
    const { focusedEditMode } = useSelector((state) => state.menu);
    const isFocused = focusedEditMode;
    const { hash: sectionId } = useLocation();
    const isMobile = useIsMobile();
    const sectionHighlightCounts = useHighlightedFieldsCounts();
    const ref = useRef<HTMLDivElement>(null);
    const [activeSection, setActiveSection] = useState<string>('');

    useEffect(() => {
        if (!sectionId) {
            return;
        }

        const section = ref.current?.querySelector(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [sectionId]);

    const shouldShowMenu = useMemo(() => showMenu && !isMobile, [showMenu, isMobile]);
    const shouldShowMobileNav = useMemo(() => showMenu && isMobile, [showMenu, isMobile]);

    // IntersectionObserver to track active section
    useEffect(() => {
        if (!ref.current || !showMenu) return;

        const sectionElements = Array.from(ref.current.querySelectorAll<HTMLElement>('[id][data-section-title]'));

        if (sectionElements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries.filter((entry) => entry.isIntersecting);
                if (visibleEntries.length > 0) {
                    // Pick the one closest to the top of the viewport
                    const topEntry = visibleEntries.reduce((closest, entry) =>
                        entry.boundingClientRect.top < closest.boundingClientRect.top ? entry : closest
                    );
                    setActiveSection(topEntry.target.id);
                }
            },
            {
                rootMargin: '-96px 0px -60% 0px',
                threshold: 0,
            }
        );

        sectionElements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [children, showMenu]);

    // Remove null values from child list. If section is readonly, then also make all of its content readonly.
    const validChildren = useMemo(() => {
        const flattened = flattenChildren(children);
        return flattened.map((val) => ({
            ...val,
            props: {
                ...val.props,
                readonly: readonly === undefined ? val.props?.readonly : readonly,
            },
        })) as React.ReactElement<SectionProps>[];
    }, [children, readonly]);

    const rowsFromChildren = useMemo(() => {
        return getRowsFromChildren(validChildren);
    }, [validChildren]);

    const handleMobileNavChange = useCallback((id: string | null) => {
        // Escaped: a custom-field section's id comes from a tenant-defined title.
        const sectionEl = id ? ref.current?.querySelector(`#${CSS.escape(id)}`) : null;
        if (sectionEl) {
            sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    if (children === null) return <div />;

    return (
        // Isolated, so a dropdown rendered inside the form body scrolls under the form's sticky
        // `ActionHeader` with the rest of the body instead of out-stacking it. The bottom padding is
        // 20px when the body closes its card, the same as a card's content box.
        <div className="isolate p-6 last:pb-5" data-form-body>
            {shouldShowMobileNav && (
                <Select value={activeSection || null} onValueChange={handleMobileNavChange}>
                    {/* `z-10` keeps the pinned picker over the fields scrolling under it, inside the
                        body's own stacking context. */}
                    <SelectTrigger size="sm" className="bg-paper sticky top-18 z-10 mb-4 w-full text-xs">
                        <SelectValue>
                            {(value: string | null) => {
                                const row = rowsFromChildren.find(
                                    (r) => getIdLink(r.noTranslate ? r.title.toLocaleLowerCase() : r.title) === value
                                );
                                if (row) {
                                    return row.noTranslate ? row.title : <FormattedMessage id={row.title} />;
                                }
                                return (
                                    <FormattedMessage id="common.jump-to-section" defaultMessage="Jump to section" />
                                );
                            }}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        {rowsFromChildren.map((row) => {
                            const id = getIdLink(row.noTranslate ? row.title.toLocaleLowerCase() : row.title);
                            return (
                                <SelectItem key={`mobile-nav-${id}`} value={id}>
                                    {row.noTranslate ? row.title : <FormattedMessage id={row.title} />}
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            )}
            <div className={shouldShowMenu ? menuGridClass(isFocused, !!rightSidebar) : undefined} ref={ref}>
                {shouldShowMenu && (
                    <ContentMenu
                        rows={rowsFromChildren}
                        activeSection={activeSection}
                        sectionErrors={sectionErrors}
                        sectionHighlightCounts={sectionHighlightCounts}
                        className={cn(
                            'sticky top-24 h-fit p-0',
                            // Focused edit mode pins the menu to the right of its own column, but
                            // only once the viewport is wide enough for the column to have slack.
                            isFocused && 'figma:mr-12 figma:w-48 figma:justify-self-end'
                        )}
                    />
                )}
                <div
                    className={cn(
                        'new:gap-(--card-gap) mx-auto box-border flex w-full flex-wrap gap-4 px-6',
                        isFocused ? 'max-w-[960px]' : 'max-w-[672px]'
                    )}
                >
                    {children}
                </div>
                {rightSidebar && <div className="sticky top-26 self-start">{rightSidebar}</div>}
            </div>
        </div>
    );
}

export default SectionLayout;
